/**
 * Form Interaction Module — captures field-level interaction signals.
 *
 * SAFETY: This module is loaded on customer websites via CDN.
 * - NEVER captures form values, passwords, or any user-entered text
 * - Only tracks behavioral metadata: timing, correction count, field identity
 * - Passive event listeners that cannot interfere with form behavior
 * - Minimal memory and CPU footprint
 */

import { BaseEvent, BaseModule } from './base';
import { Namespace } from './types';

interface FormFieldInteraction extends BaseEvent {
  fieldSelector: string;
  fieldType: string;
  fieldName: string;
  fieldLabel: string;
  focusTimestamp: number;
  blurTimestamp: number;
  dwellMs: number;
  corrections: number;
  fieldIndex: number;
  fillOrder: number;
  formSelector: string;
  formId: string;
}

interface FormSubmissionEvent {
  timestamp: number;
  where: string;
  formSelector: string;
  formId: string;
  fieldCount: number;
}

interface ActiveField {
  selector: string;
  type: string;
  name: string;
  label: string;
  focusTs: number;
  keyCount: number;
  deleteCount: number;
  formSelector: string;
  formId: string;
  fieldIndex: number;
}

const FIELD_TAGS = new Set(['INPUT', 'TEXTAREA', 'SELECT']);
const IGNORED_INPUT_TYPES = new Set([
  'submit',
  'button',
  'reset',
  'hidden',
  'image',
]);
const MAX_EVENTS_PER_SYNC = 200;

function isFormField(el: Element): boolean {
  if (!FIELD_TAGS.has(el.tagName)) return false;
  if (el.tagName === 'INPUT') {
    const type = (el as HTMLInputElement).type?.toLowerCase() || 'text';
    if (IGNORED_INPUT_TYPES.has(type)) return false;
  }
  return true;
}

function safeFieldSelector(el: Element): string {
  try {
    const tag = el.tagName.toLowerCase();
    const id = el.id ? `#${el.id}` : '';
    const name = (el as HTMLInputElement).name
      ? `[name="${(el as HTMLInputElement).name}"]`
      : '';
    const type =
      el.tagName === 'INPUT'
        ? `[type="${(el as HTMLInputElement).type || 'text'}"]`
        : '';
    return `${tag}${id}${name}${type}`;
  } catch {
    return el.tagName?.toLowerCase() || 'unknown';
  }
}

function getFieldLabel(el: Element): string {
  try {
    if (el.id) {
      const label = document.querySelector(`label[for="${el.id}"]`);
      if (label?.textContent) return label.textContent.trim().slice(0, 100);
    }
    const ariaLabel = el.getAttribute('aria-label');
    if (ariaLabel) return ariaLabel.trim().slice(0, 100);
    const placeholder = (el as HTMLInputElement).placeholder;
    if (placeholder) return placeholder.trim().slice(0, 100);
    const parent = el.closest('label');
    if (parent?.textContent) {
      const text = parent.textContent.trim().slice(0, 100);
      if (text) return text;
    }
    const prev = el.previousElementSibling;
    if (prev?.tagName === 'LABEL' && prev.textContent) {
      return prev.textContent.trim().slice(0, 100);
    }
  } catch {
    // Silently fail
  }
  return '';
}

function safeFormSelector(form: HTMLFormElement | null): string {
  if (!form) return '';
  try {
    const id = form.id ? `#${form.id}` : '';
    const name = form.name ? `[name="${form.name}"]` : '';
    const method = form.method ? `[method="${form.method}"]` : '';
    let action = '';
    if (form.action) {
      try {
        const url = new URL(form.action, window.location.origin);
        if (url.pathname && url.pathname !== '/') {
          action = `[action="${url.pathname}"]`;
        }
      } catch {
        action = '[action]';
      }
    }
    return `form${id}${name}${action}${method}`;
  } catch {
    return 'form';
  }
}

function getFieldIndex(el: Element, form: HTMLFormElement | null): number {
  if (!form) return 0;
  try {
    const fields = Array.from(form.elements).filter(isFormField);
    return fields.indexOf(el);
  } catch {
    return 0;
  }
}

export class FormModule extends BaseModule<FormFieldInteraction> {
  namespace: Namespace = 'form';

  private activeField: ActiveField | null = null;
  private handleFocusIn: ((e: FocusEvent) => void) | null = null;
  private handleFocusOut: ((e: FocusEvent) => void) | null = null;
  private handleKeyDown: ((e: KeyboardEvent) => void) | null = null;
  private handleSubmit: ((e: Event) => void) | null = null;
  private fillSequence = 0;
  private submissions: FormSubmissionEvent[] = [];

  onStart() {
    this.handleFocusIn = (e: FocusEvent) => {
      const target = e.target;
      if (!target || !(target instanceof Element)) return;
      if (!isFormField(target)) return;

      const form = (target as HTMLInputElement).form ?? null;

      this.activeField = {
        selector: safeFieldSelector(target),
        type:
          target.tagName === 'INPUT'
            ? (target as HTMLInputElement).type || 'text'
            : target.tagName.toLowerCase(),
        name: (target as HTMLInputElement).name || '',
        label: getFieldLabel(target),
        focusTs: Date.now(),
        keyCount: 0,
        deleteCount: 0,
        formSelector: safeFormSelector(form),
        formId: form?.id || '',
        fieldIndex: getFieldIndex(target, form),
      };
    };

    this.handleFocusOut = (e: FocusEvent) => {
      const target = e.target;
      if (!target || !(target instanceof Element)) return;
      if (!this.activeField) return;

      const now = Date.now();
      const af = this.activeField;
      const dwell = now - af.focusTs;

      if (dwell < 50) {
        this.activeField = null;
        return;
      }

      this.fillSequence++;

      this.state.push({
        timestamp: af.focusTs,
        where: window.location.pathname,
        fieldSelector: af.selector,
        fieldType: af.type,
        fieldName: af.name,
        fieldLabel: af.label,
        focusTimestamp: af.focusTs,
        blurTimestamp: now,
        dwellMs: dwell,
        corrections: af.deleteCount,
        fieldIndex: af.fieldIndex,
        fillOrder: this.fillSequence,
        formSelector: af.formSelector,
        formId: af.formId,
      });

      this.activeField = null;
    };

    this.handleKeyDown = (e: KeyboardEvent) => {
      if (!this.activeField) return;
      this.activeField.keyCount++;
      if (e.key === 'Backspace' || e.key === 'Delete') {
        this.activeField.deleteCount++;
      }
    };

    this.handleSubmit = (e: Event) => {
      const form = e.target as HTMLFormElement;
      if (!form || form.tagName !== 'FORM') return;
      const fields = Array.from(form.elements).filter(isFormField);
      this.submissions.push({
        timestamp: Date.now(),
        where: window.location.pathname,
        formSelector: safeFormSelector(form),
        formId: form.id || '',
        fieldCount: fields.length,
      });
      this.state.push({
        fieldSelector: '__submission_marker__',
        fieldType: 'submit',
        fieldName: '',
        fieldLabel: '',
        focusTimestamp: Date.now(),
        blurTimestamp: Date.now(),
        dwellMs: 0,
        corrections: 0,
        fieldIndex: -1,
        fillOrder: -1,
        formSelector: safeFormSelector(form),
        formId: form.id || '',
        timestamp: Date.now(),
        where: window.location.pathname,
      });
    };

    try {
      document.addEventListener('focusin', this.handleFocusIn, {
        passive: true,
      });
      document.addEventListener('focusout', this.handleFocusOut, {
        passive: true,
      });
      document.addEventListener('keydown', this.handleKeyDown, {
        passive: true,
      });
      document.addEventListener('submit', this.handleSubmit, {
        passive: true,
      });
    } catch {
      // Silently fail — never break customer pages
    }
  }

  onStop() {
    try {
      if (this.handleFocusIn) {
        document.removeEventListener('focusin', this.handleFocusIn);
      }
      if (this.handleFocusOut) {
        document.removeEventListener('focusout', this.handleFocusOut);
      }
      if (this.handleKeyDown) {
        document.removeEventListener('keydown', this.handleKeyDown);
      }
      if (this.handleSubmit) {
        document.removeEventListener('submit', this.handleSubmit);
      }
    } catch {
      // Silently fail
    }
    this.handleFocusIn = null;
    this.handleFocusOut = null;
    this.handleKeyDown = null;
    this.handleSubmit = null;
    this.activeField = null;
  }

  buildRequest(state: FormFieldInteraction[]) {
    const realState = state.filter(
      (s) => s.fieldSelector !== '__submission_marker__',
    );
    const capped = realState.slice(0, MAX_EVENTS_PER_SYNC);
    const subs = this.submissions.splice(0);
    return { state: capped, submissions: subs.length > 0 ? subs : undefined };
  }

  purgeState() {
    this.state = [];
    this.submissions = [];
    this.activeField = null;
  }
}
